import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import FilterSidebar from '../components/FilterSidebar.jsx';
import useCampaign from '../hooks/useCampaign.js';

export default function ListPage() {
  const { campaignId } = useCampaign();
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ field: 'annual_revenue', dir: 'desc' });

  const { data, isLoading } = useQuery({
    queryKey: ['businesses', filters, campaignId],
    queryFn: () =>
      api.listBusinesses({
        ...filters,
        campaign_id: campaignId,
        limit: 2000,
      }),
  });

  const businesses = data?.businesses || [];

  // Client-side sort
  const sorted = [...businesses].sort((a, b) => {
    const av = a[sort.field];
    const bv = b[sort.field];
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number') {
      return sort.dir === 'asc' ? av - bv : bv - av;
    }
    return sort.dir === 'asc' ? String(av).localeCompare(bv) : String(bv).localeCompare(av);
  });

  function headerClick(field) {
    if (sort.field === field) {
      setSort({ field, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      setSort({ field, dir: 'desc' });
    }
  }

  return (
    <div className="h-full flex">
      <FilterSidebar filters={filters} onChange={setFilters} campaignId={campaignId} />

      <div className="flex-1 overflow-auto">
        <div className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {isLoading ? 'Loading...' : `${sorted.length} businesses`}
            </h2>
          </div>

          <div className="bg-white rounded shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <Th label="Name" field="name" sort={sort} onClick={headerClick} />
                  <Th label="Category" field="primary_sic_description" sort={sort} onClick={headerClick} />
                  <Th label="Corridor" field="corridor" sort={sort} onClick={headerClick} />
                  <Th label="Revenue" field="annual_revenue" sort={sort} onClick={headerClick} />
                  <Th label="Employees" field="employee_count" sort={sort} onClick={headerClick} />
                  <Th label="Credit" field="credit_rating" sort={sort} onClick={headerClick} />
                  <Th label="Contact" field="contact_name" sort={sort} onClick={headerClick} />
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((b) => (
                  <tr key={b.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <Link
                        to={`/business/${b.id}`}
                        className="text-ccf-primary hover:underline font-medium"
                      >
                        {b.name}
                      </Link>
                      <div className="text-xs text-gray-500">{b.address}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700">{b.primary_sic_description || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">{b.corridor?.replace('_', ' ') || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">
                      {b.annual_revenue ? `$${(b.annual_revenue / 1000).toFixed(0)}K` : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700">{b.employee_range || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">{b.credit_rating || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">
                      {b.contact_name || '—'}
                      {b.contact_title && (
                        <div className="text-[10px] text-gray-500">{b.contact_title}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {b.prospect_status ? (
                        <StatusPill status={b.prospect_status} />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ label, field, sort, onClick }) {
  const active = sort.field === field;
  return (
    <th
      onClick={() => onClick(field)}
      className="px-3 py-2 font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
    >
      {label} {active ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );
}

function StatusPill({ status }) {
  const colors = {
    identified: 'bg-gray-100 text-gray-700',
    contacted: 'bg-blue-100 text-blue-700',
    interested: 'bg-purple-100 text-purple-700',
    committed: 'bg-emerald-100 text-emerald-700',
    declined: 'bg-red-100 text-red-700',
    no_response: 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded ${colors[status] || 'bg-gray-100'}`}>
      {status}
    </span>
  );
}
